(() => {
  const roots = document.querySelectorAll('[data-mcc-collection]');

  roots.forEach((root) => {
    const filterForm = root.querySelector('[data-mcc-filter-form]');
    const sortForms = root.querySelectorAll('[data-mcc-sort-form]');
    const drawer = root.querySelector('[data-mcc-filter-drawer]');
    const drawerContent = root.querySelector('.mcc-collection__drawer-content');
    const openDrawer = root.querySelector('[data-mcc-filter-open]');
    const closeDrawerButtons = root.querySelectorAll('[data-mcc-filter-close]');

    const submitForm = (form) => {
      if (!form) return;
      const formData = new FormData(form);
      const params = new URLSearchParams(window.location.search);

      Array.from(params.keys()).forEach((key) => {
        if (key.startsWith('filter.') || key === 'sort_by' || key === 'page') {
          params.delete(key);
        }
      });

      formData.forEach((value, key) => {
        if (value !== '') {
          params.append(key, value);
        }
      });

      const query = params.toString();
      window.location.href = `${window.location.pathname}${query ? `?${query}` : ''}`;
    };

    const initPriceRange = (form) => {
      form.querySelectorAll('[data-mcc-price-range]').forEach((range) => {
        const minRange = range.querySelector('[data-mcc-price-min]');
        const maxRange = range.querySelector('[data-mcc-price-max]');
        const minField = range.querySelector('[data-mcc-price-min-field]');
        const maxField = range.querySelector('[data-mcc-price-max-field]');
        const track = range.querySelector('[data-mcc-price-track]');

        if (!minRange || !maxRange || !minField || !maxField || !track) return;

        const minLimit = Number(minRange.min);
        const maxLimit = Number(maxRange.max);

        const updateTrack = () => {
          const minValue = Number(minRange.value);
          const maxValue = Number(maxRange.value);
          const span = maxLimit - minLimit || 1;
          const start = ((minValue - minLimit) / span) * 100;
          const end = ((maxValue - minLimit) / span) * 100;

          track.style.setProperty('--mcc-range-start', `${start}%`);
          track.style.setProperty('--mcc-range-end', `${end}%`);
        };

        const syncFields = () => {
          const minValue = Number(minRange.value);
          const maxValue = Number(maxRange.value);

          minField.value = minValue > minLimit ? minValue : '';
          maxField.value = maxValue < maxLimit ? maxValue : '';
          updateTrack();
        };

        const normalize = (event) => {
          const minValue = Number(minRange.value);
          const maxValue = Number(maxRange.value);

          if (minValue > maxValue) {
            if (event.target === minRange) {
              maxRange.value = minRange.value;
            } else {
              minRange.value = maxRange.value;
            }
          }

          syncFields();
        };

        minRange.addEventListener('input', normalize);
        maxRange.addEventListener('input', normalize);
        minRange.addEventListener('change', () => submitForm(form));
        maxRange.addEventListener('change', () => submitForm(form));
        minField.addEventListener('input', () => {
          if (minField.value !== '') {
            minRange.value = minField.value;
          } else {
            minRange.value = minLimit;
          }
          normalize({ target: minRange });
        });
        maxField.addEventListener('input', () => {
          if (maxField.value !== '') {
            maxRange.value = maxField.value;
          } else {
            maxRange.value = maxLimit;
          }
          normalize({ target: maxRange });
        });

        syncFields();
      });
    };

    const parseDimension = (raw) => {
      if (raw == null || raw === '') return NaN;

      const trimmed = String(raw).trim();
      const plain = trimmed.match(/^(\d+(?:\.\d+)?)$/);

      if (plain) {
        return Number(plain[1]);
      }

      const feetInches = trimmed.match(/^(\d+)\s*[''′]\s*(\d+(?:\.\d+)?)?\s*["″]?$/);

      if (feetInches) {
        const feet = Number(feetInches[1]);
        const inches = feetInches[2] ? Number(feetInches[2]) : 0;
        return feet + inches / 12;
      }

      const numeric = Number(trimmed.replace(/[^0-9.-]/g, ''));
      return Number.isFinite(numeric) ? numeric : NaN;
    };

    const readDimension = (...candidates) => {
      for (const candidate of candidates) {
        const parsed = parseDimension(candidate);
        if (Number.isFinite(parsed)) {
          return parsed;
        }
      }

      return NaN;
    };

    const initNumericRanges = (form) => {
      form.querySelectorAll('[data-mcc-numeric-range]').forEach((container) => {
        const minRange = container.querySelector('[data-mcc-numeric-min]');
        const maxRange = container.querySelector('[data-mcc-numeric-max]');
        const minField = container.querySelector('[data-mcc-numeric-min-field]');
        const maxField = container.querySelector('[data-mcc-numeric-max-field]');
        const track = container.querySelector('[data-mcc-numeric-track]');
        const boundMin = container.querySelector('[data-mcc-numeric-bound-min]');
        const boundMax = container.querySelector('[data-mcc-numeric-bound-max]');
        const checkboxes = Array.from(container.querySelectorAll('[data-mcc-numeric-checkbox]'));

        if (!minRange || !maxRange || !minField || !maxField || !track || checkboxes.length === 0) {
          return;
        }

        const options = checkboxes
          .map((checkbox, index) => ({
            checkbox,
            index,
            label: checkbox.dataset.label || '',
            value: checkbox.value || '',
            number: readDimension(checkbox.dataset.number, checkbox.dataset.label, checkbox.value),
            active: checkbox.checked,
          }))
          .sort((left, right) => {
            if (Number.isFinite(left.number) && Number.isFinite(right.number)) {
              return left.number - right.number;
            }

            return left.index - right.index;
          });

        const numbers = options.map((option) => option.number).filter((value) => Number.isFinite(value));

        if (numbers.length === 0) {
          return;
        }

        const numMin = numbers[0];
        const numMax = numbers[numbers.length - 1];
        const useIntegerStep = numbers.every((value) => Math.abs(value - Math.round(value)) < 0.001);
        const step = useIntegerStep ? 1 : 0.1;

        const snapMin = (value) => {
          let snapped = numbers[0];

          numbers.forEach((number) => {
            if (number <= value) {
              snapped = number;
            }
          });

          return snapped;
        };

        const snapMax = (value) => {
          for (let index = 0; index < numbers.length; index += 1) {
            if (numbers[index] >= value) {
              return numbers[index];
            }
          }

          return numbers[numbers.length - 1];
        };

        const formatDragValue = (value) => {
          if (useIntegerStep) {
            return String(Math.round(value));
          }

          return String(Math.round(value * 10) / 10);
        };

        const formatSnappedValue = (value) => {
          const matchingOption = options.find((option) => option.number === value);
          return matchingOption ? matchingOption.label : formatDragValue(value);
        };

        const getInitialRange = () => {
          const activeNumbers = options
            .filter((option) => option.active || option.checkbox.checked)
            .map((option) => option.number)
            .filter((value) => Number.isFinite(value));

          if (activeNumbers.length === 0) {
            return { from: numMin, to: numMax };
          }

          return {
            from: Math.min(...activeNumbers),
            to: Math.max(...activeNumbers),
          };
        };

        const initialRange = getInitialRange();

        minRange.min = String(numMin);
        minRange.max = String(numMax);
        minRange.step = String(step);
        maxRange.min = String(numMin);
        maxRange.max = String(numMax);
        maxRange.step = String(step);
        minRange.value = String(initialRange.from);
        maxRange.value = String(initialRange.to);

        if (boundMin) {
          boundMin.textContent = options[0]?.label || '';
        }

        if (boundMax) {
          boundMax.textContent = options[options.length - 1]?.label || '';
        }

        const updateTrack = (fromValue, toValue) => {
          const span = numMax - numMin || 1;
          const start = ((fromValue - numMin) / span) * 100;
          const end = ((toValue - numMin) / span) * 100;

          track.style.setProperty('--mcc-range-start', `${start}%`);
          track.style.setProperty('--mcc-range-end', `${end}%`);
        };

        const applyCheckboxState = (fromNum, toNum) => {
          const isFullRange = fromNum <= numMin && toNum >= numMax;

          options.forEach((option) => {
            if (isFullRange || !Number.isFinite(option.number)) {
              option.checkbox.checked = false;
              return;
            }

            option.checkbox.checked = option.number >= fromNum && option.number <= toNum;
          });
        };

        const syncDisplay = (fromValue, toValue, { snapped = false } = {}) => {
          minField.value = snapped ? formatSnappedValue(fromValue) : formatDragValue(fromValue);
          maxField.value = snapped ? formatSnappedValue(toValue) : formatDragValue(toValue);
          updateTrack(fromValue, toValue);
        };

        const commitRange = (fromValue, toValue) => {
          let fromNum = snapMin(fromValue);
          let toNum = snapMax(toValue);

          if (fromNum > toNum) {
            fromNum = snapMin(toNum);
            toNum = snapMax(fromValue);
          }

          minRange.value = String(fromNum);
          maxRange.value = String(toNum);
          syncDisplay(fromNum, toNum, { snapped: true });
          applyCheckboxState(fromNum, toNum);
        };

        const onSliderInput = (event) => {
          let fromValue = Number(minRange.value);
          let toValue = Number(maxRange.value);

          if (fromValue > toValue) {
            if (event.target === minRange) {
              toValue = fromValue;
              maxRange.value = String(toValue);
            } else {
              fromValue = toValue;
              minRange.value = String(fromValue);
            }
          }

          syncDisplay(fromValue, toValue);
        };

        const onSliderChange = () => {
          commitRange(Number(minRange.value), Number(maxRange.value));
          submitForm(form);
        };

        const commitField = (field, rangeInput, snapFn) => {
          const parsed = readDimension(field.value);

          if (!Number.isFinite(parsed)) {
            commitRange(Number(minRange.value), Number(maxRange.value));
            return;
          }

          const snapped = snapFn(parsed);
          rangeInput.value = String(snapped);

          if (rangeInput === minRange) {
            commitRange(snapped, Number(maxRange.value));
          } else {
            commitRange(Number(minRange.value), snapped);
          }

          submitForm(form);
        };

        const bringRangeToFront = (activeRange) => {
          minRange.style.zIndex = activeRange === minRange ? '5' : '2';
          maxRange.style.zIndex = activeRange === maxRange ? '5' : '3';
        };

        minRange.addEventListener('pointerdown', () => bringRangeToFront(minRange));
        maxRange.addEventListener('pointerdown', () => bringRangeToFront(maxRange));
        minRange.addEventListener('input', onSliderInput);
        maxRange.addEventListener('input', onSliderInput);
        minRange.addEventListener('change', onSliderChange);
        maxRange.addEventListener('change', onSliderChange);

        minField.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            minField.blur();
          }
        });
        maxField.addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            maxField.blur();
          }
        });
        minField.addEventListener('blur', () => commitField(minField, minRange, snapMin));
        maxField.addEventListener('blur', () => commitField(maxField, maxRange, snapMax));

        commitRange(initialRange.from, initialRange.to);
      });
    };

    const initControls = (form) => {
      initPriceRange(form);
      initNumericRanges(form);
    };

    if (filterForm) {
      initControls(filterForm);
      filterForm.addEventListener('change', (event) => {
        if (event.target.matches('[data-mcc-price-min], [data-mcc-price-max], [data-mcc-numeric-min], [data-mcc-numeric-max], [data-mcc-numeric-min-field], [data-mcc-numeric-max-field], [data-mcc-numeric-checkbox]')) {
          return;
        }
        submitForm(filterForm);
      });
    }

    sortForms.forEach((form) => {
      form.addEventListener('change', () => submitForm(form));
    });

    if (drawer && drawerContent && filterForm) {
      const clonedForm = filterForm.cloneNode(true);
      clonedForm.removeAttribute('data-mcc-filter-form');
      drawerContent.appendChild(clonedForm);
      initControls(clonedForm);
      clonedForm.addEventListener('change', (event) => {
        if (event.target.matches('[data-mcc-price-min], [data-mcc-price-max], [data-mcc-numeric-min], [data-mcc-numeric-max], [data-mcc-numeric-min-field], [data-mcc-numeric-max-field], [data-mcc-numeric-checkbox]')) {
          return;
        }
        submitForm(clonedForm);
      });
    }

    const setDrawerOpen = (isOpen) => {
      if (!drawer) return;
      drawer.classList.toggle('is-open', isOpen);
      drawer.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
      document.documentElement.classList.toggle('mcc-collection-drawer-open', isOpen);
    };

    if (openDrawer) {
      openDrawer.addEventListener('click', () => setDrawerOpen(true));
    }

    closeDrawerButtons.forEach((button) => {
      button.addEventListener('click', () => setDrawerOpen(false));
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        setDrawerOpen(false);
      }
    });

    const categorySlider = root.querySelector('[data-mcc-category-slider]');

    if (categorySlider) {
      const track = categorySlider.querySelector('[data-mcc-category-track]');
      const nextButton = categorySlider.querySelector('[data-mcc-category-next]');

      const updateCategoryNav = () => {
        if (!track || !nextButton) return;

        const canScroll = track.scrollWidth > track.clientWidth + 1;
        const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;

        nextButton.hidden = !canScroll || atEnd;
      };

      const scrollCategories = () => {
        if (!track) return;

        const item = track.querySelector('.mcc-collection__category');
        const gap = Number.parseFloat(window.getComputedStyle(track).columnGap || window.getComputedStyle(track).gap) || 10;
        const scrollAmount = item ? item.offsetWidth + gap : track.clientWidth * 0.75;

        track.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      };

      nextButton?.addEventListener('click', scrollCategories);
      track?.addEventListener('scroll', updateCategoryNav, { passive: true });
      window.addEventListener('resize', updateCategoryNav);
      updateCategoryNav();
    }

    const description = root.querySelector('[data-mcc-description]');

    if (description) {
      const summary = description.querySelector('summary');
      const content = description.querySelector('.mcc-collection__description-content');

      const onTransitionEnd = (callback) => {
        const handler = (event) => {
          if (event.target !== content || event.propertyName !== 'height') return;
          content.removeEventListener('transitionend', handler);
          callback();
        };

        content.addEventListener('transitionend', handler);
      };

      summary?.addEventListener('click', (event) => {
        event.preventDefault();

        if (description.classList.contains('is-animating')) return;

        description.classList.add('is-animating');

        if (description.open) {
          content.style.height = `${content.scrollHeight}px`;
          content.offsetHeight;
          content.style.height = '0';

          onTransitionEnd(() => {
            description.removeAttribute('open');
            description.classList.remove('is-animating');
          });
        } else {
          description.setAttribute('open', '');
          content.style.height = '0';
          content.offsetHeight;
          content.style.height = `${content.scrollHeight}px`;

          onTransitionEnd(() => {
            content.style.height = 'auto';
            description.classList.remove('is-animating');
          });
        }
      });
    }

    const initQuickShop = () => {
      if (!root.dataset.quickShopEnabled || typeof theme === 'undefined' || typeof theme.preloadProductModal !== 'function') {
        return;
      }

      const preloadCard = (card) => {
        if (card.dataset.quickShopPreloaded === 'true') return;

        const productId = card.dataset.productId;
        const handle = card.dataset.productHandle;
        const button = card.querySelector('.quick-product__btn');

        if (!productId || !handle || !button) return;
        if (!document.getElementById(`QuickShopModal-${productId}`)) return;

        card.dataset.quickShopPreloaded = 'true';
        theme.preloadProductModal(handle, productId, button);
      };

      const isTouchOrMobile = () => theme.config?.isTouch || theme.config?.bpSmall;

      const bindClickFallback = (card, button) => {
        button.addEventListener('click', (event) => {
          if (!button.classList.contains('btn--not-ready')) return;

          event.preventDefault();
          event.stopImmediatePropagation();

          preloadCard(card);

          let attempts = 0;
          const openWhenReady = window.setInterval(() => {
            attempts += 1;

            if (!button.classList.contains('btn--not-ready')) {
              window.clearInterval(openWhenReady);
              button.click();
              return;
            }

            if (attempts >= 50) {
              window.clearInterval(openWhenReady);
            }
          }, 100);
        }, true);
      };

      root.querySelectorAll('.mcc-product-card[data-product-handle]').forEach((card) => {
        const button = card.querySelector('.quick-product__btn');
        if (!button) return;

        bindClickFallback(card, button);

        if (isTouchOrMobile()) {
          if ('IntersectionObserver' in window) {
            const observer = new IntersectionObserver((entries) => {
              entries.forEach((entry) => {
                if (!entry.isIntersecting) return;
                preloadCard(card);
                observer.unobserve(card);
              });
            }, { rootMargin: '120px' });

            observer.observe(card);
          } else {
            preloadCard(card);
          }

          return;
        }

        const preloadOnce = () => {
          preloadCard(card);
          card.removeEventListener('mouseover', preloadOnce);
          card.removeEventListener('focusin', preloadOnce);
        };

        card.addEventListener('mouseover', preloadOnce);
        card.addEventListener('focusin', preloadOnce);
      });
    };

    initQuickShop();
  });
})();
